import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { logger } from '@/lib/logger'
import { getDataProvider } from '@/lib/dataProvider'

// TypeScript interfaces based on the database schema
export interface PlaylistNode {
  id: string
  user_id: string
  name: string
  shuffle: boolean
  repeat_mode: 'none' | 'all' | 'one'
  volume: number
  playback_target: string | null
  position_x: number
  position_y: number
  width: number
  height: number
  is_collapsed: boolean
  container_id: string | null
  created_at: string
  updated_at: string
}

export interface CreatePlaylistNodeData {
  name: string
  position_x?: number
  position_y?: number
  width?: number
  height?: number
  is_collapsed?: boolean
}

export interface UpdatePlaylistNodeData {
  name?: string
  shuffle?: boolean
  repeat_mode?: 'none' | 'all' | 'one'
  volume?: number
  playback_target?: string | null
  position_x?: number
  position_y?: number
  width?: number
  height?: number
  is_collapsed?: boolean
  container_id?: string | null
}

export const usePlaylistNodesStore = defineStore('playlistNodes', () => {
  const authStore = useAuthStore()
  
  // State
  const playlistNodes = ref<Map<string, PlaylistNode>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  // Getters
  const allPlaylistNodes = computed(() => {
    return Array.from(playlistNodes.value.values())
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  const getPlaylistNodeById = computed(() => (id: string) => {
    return playlistNodes.value.get(id)
  })

  const isPlaylistNodeLoading = computed(() => (id: string) => {
    return loadingStates.value.get(id) || false
  })

  // Fetch all playlist nodes for the user
  const fetchPlaylistNodes = async (): Promise<{ success: boolean; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const data = await provider.query<PlaylistNode>('playlist_nodes', {
        orderBy: [{ column: 'created_at', ascending: true }]
      })

      // Update state
      playlistNodes.value.clear()
      for (const node of data) {
        playlistNodes.value.set(node.id, node)
      }

      logger.info('🎵 Fetched playlist nodes:', data.length)
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Create a new playlist node
  const createPlaylistNode = async (data: CreatePlaylistNodeData): Promise<{ success: boolean; data?: PlaylistNode; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true

    try {
      const provider = getDataProvider()
      const insertData = {
        user_id: authStore.user.id,
        name: data.name,
        position_x: data.position_x ?? 0,
        position_y: data.position_y ?? 0,
        width: data.width ?? 300,
        height: data.height ?? 250
      }

      const result = await provider.insert<PlaylistNode>('playlist_nodes', insertData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      playlistNodes.value.set(result.data.id, result.data)
      logger.info('🎵 Created playlist node:', result.data.name)
      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Update a playlist node
  const updatePlaylistNode = async (id: string, updates: UpdatePlaylistNodeData): Promise<{ success: boolean; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.update<PlaylistNode>('playlist_nodes', id, updates)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update local state
      const existingNode = playlistNodes.value.get(id)
      if (existingNode) {
        playlistNodes.value.set(id, { ...existingNode, ...updates, updated_at: new Date().toISOString() })
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  // Delete a playlist node
  const deletePlaylistNode = async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.delete('playlist_nodes', id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      playlistNodes.value.delete(id)
      logger.info('🎵 Deleted playlist node')
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  // Duplicate a playlist node (including music nodes inside it)
  const duplicatePlaylistNode = async (id: string): Promise<{ success: boolean; data?: PlaylistNode; error?: string }> => {
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    const original = playlistNodes.value.get(id)
    if (!original) {
      return { success: false, error: 'Playlist not found' }
    }

    isLoading.value = true

    try {
      const provider = getDataProvider()
      
      // Create new playlist node
      const newNodeData = {
        user_id: authStore.user.id,
        name: `${original.name} (Copy)`,
        shuffle: original.shuffle,
        repeat_mode: original.repeat_mode,
        volume: original.volume,
        playback_target: original.playback_target,
        position_x: original.position_x + 50,
        position_y: original.position_y + 50,
        width: original.width,
        height: original.height,
        container_id: original.container_id
      }

      const result = await provider.insert<PlaylistNode>('playlist_nodes', newNodeData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      playlistNodes.value.set(result.data.id, result.data)

      // Also duplicate any music nodes that are inside this playlist
      const musicNodes = await provider.query<any>('music_nodes', {
        filters: [{ column: 'playlist_id', operator: 'eq', value: id }]
      })

      if (musicNodes && musicNodes.length > 0) {
        for (const musicNode of musicNodes) {
          await provider.insert('music_nodes', {
            user_id: authStore.user.id,
            name: musicNode.name,
            storage_path: musicNode.storage_path,
            file_name: musicNode.file_name,
            file_size: musicNode.file_size,
            duration: musicNode.duration,
            volume: musicNode.volume,
            loop: musicNode.loop,
            position_x: musicNode.position_x,
            position_y: musicNode.position_y,
            playback_target: musicNode.playback_target,
            playlist_id: result.data.id // Set to new playlist
          })
        }
      }

      logger.info('🎵 Duplicated playlist node:', result.data.name)
      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  // Toggle shuffle
  const toggleShuffle = async (id: string): Promise<{ success: boolean; error?: string }> => {
    const playlist = playlistNodes.value.get(id)
    if (!playlist) {
      return { success: false, error: 'Playlist not found' }
    }

    return updatePlaylistNode(id, { shuffle: !playlist.shuffle })
  }

  // Set repeat mode
  const setRepeatMode = async (id: string, mode: 'none' | 'all' | 'one'): Promise<{ success: boolean; error?: string }> => {
    return updatePlaylistNode(id, { repeat_mode: mode })
  }

  return {
    // State
    playlistNodes: computed(() => playlistNodes.value),
    error: computed(() => error.value),
    isLoading: computed(() => isLoading.value),

    // Getters
    allPlaylistNodes,
    getPlaylistNodeById,
    isPlaylistNodeLoading,

    // Actions
    fetchPlaylistNodes,
    createPlaylistNode,
    updatePlaylistNode,
    deletePlaylistNode,
    duplicatePlaylistNode,
    toggleShuffle,
    setRepeatMode
  }
})
